import { useState } from 'react';

import HubRoundedIcon from '@mui/icons-material/HubRounded';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';
import { getIntlLocale } from '@/shared/i18n';
import { ErrorState, LoadingState } from '@/shared/ui';

import {
  ConfiguredSecretField,
  FeedbackSnackbar,
  SecondaryFormActions,
  SettingsCard,
  SwitchField,
  testAiAgentProviderConnection,
  useAiAgentSettings,
  useDomainPermissions,
  useFeedback,
  useSettingsForm,
  useUpdateAiAgentSettings,
} from '@/features/store-settings';
import type {
  AiAgentSettings,
  AiProviderCheckRequest,
  AiProviderCheckResult,
  UpdateAiAgentSettingsRequest,
} from '@/features/store-settings';

import type {
  ChatbotAiMode,
  ChatbotCapabilityState,
  ChatbotChannel,
  ChatbotChannelAvailability,
  ChatbotProviderDescriptor,
} from '../../model/types';
import { AiStatusLine } from './AiStatusLine';
import { ModelField } from './ModelField';
import { ProviderCheckPanel } from './ProviderCheckPanel';

export interface AiProviderCardProps {
  aiCapability?: ChatbotCapabilityState;
  availabilityChannels: ChatbotChannelAvailability[];
  availabilityLoading: boolean;
  availabilityError: boolean;
  effectiveAiMode: Record<ChatbotChannel, ChatbotAiMode | undefined>;
  effectiveOverriddenFields: Record<ChatbotChannel, string[] | undefined>;
  providers: ChatbotProviderDescriptor[] | undefined;
  dictionariesLoading: boolean;
  dictionariesError: boolean;
  onRetryDictionaries: () => void;
  canViewTenant: boolean;
  onChangeAiMode: (scope: ChatbotChannel | 'GENERAL') => void;
}

function formatTimestamp(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function buildAiAgentUpdate(
  draft: AiAgentSettings,
  loaded: AiAgentSettings,
  providerPending: boolean,
  credentialCandidate: string,
): UpdateAiAgentSettingsRequest {
  const body: UpdateAiAgentSettingsRequest = { version: loaded.version };
  if (draft.enabled !== loaded.enabled) body.enabled = draft.enabled;
  if (draft.externalProviderAllowed !== loaded.externalProviderAllowed) {
    body.externalProviderAllowed = draft.externalProviderAllowed;
  }
  if (
    !providerPending &&
    (draft.provider !== loaded.provider || draft.model !== loaded.model) &&
    draft.provider
  ) {
    body.provider = draft.provider;
    body.model = draft.model ?? '';
  }
  const trimmedCredential = credentialCandidate.trim();
  if (trimmedCredential !== '') body.credential = trimmedCredential;
  if (providerPending) body.resetFields = ['provider'];
  return body;
}

/**
 * AI provider card (design-chatbot-ai-provider-card.md, contract delta 2026-09-23) — rebuilt against
 * the hub's real `agent_settings` row (BFF adr-merchant-portal-022 D5). Absorbs the former standalone
 * `/settings/ai-agents` page. `communicationStyle`/`maxTokens`/`allowedTools` are stored by the hub
 * but read by no pipeline code and are never rendered nor sent (§7.7 — a control that changes
 * nothing is a false promise).
 */
export function AiProviderCard({
  aiCapability,
  availabilityChannels,
  availabilityLoading,
  availabilityError,
  effectiveAiMode,
  effectiveOverriddenFields,
  providers,
  dictionariesLoading,
  dictionariesError,
  onRetryDictionaries,
  canViewTenant,
  onChangeAiMode,
}: AiProviderCardProps) {
  const { t } = useTranslation('chatbot');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('ai-agents');
  const query = useAiAgentSettings();
  const update = useUpdateAiAgentSettings();
  const form = useSettingsForm(query.data);
  const [providerPending, setProviderPending] = useState(false);
  const [credentialCandidate, setCredentialCandidate] = useState('');
  const [externalConfirmOpen, setExternalConfirmOpen] = useState(false);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const checkMutation = useMutation<
    AiProviderCheckResult,
    NormalizedApiError,
    AiProviderCheckRequest | undefined
  >({
    mutationFn: (body) => testAiAgentProviderConnection(body),
  });

  if (!canView) {
    return (
      <SettingsCard title={t('ai.title')} icon={<HubRoundedIcon />} divided={false}>
        <Chip size="small" variant="outlined" label={t('ai.noAccess')} />
      </SettingsCard>
    );
  }

  const draft = form.draft;
  const displayedProvider = providerPending ? null : (draft?.provider ?? null);
  const dirty = form.dirty || providerPending || credentialCandidate.trim() !== '';
  const disabled = !canManage;

  const reset = () => {
    form.reset();
    setProviderPending(false);
    setCredentialCandidate('');
  };

  const effectiveProviderKey = displayedProvider ?? query.data?.platformDefaultProvider;
  const effectiveProviderDescriptor = providers?.find((p) => p.key === effectiveProviderKey);
  const isExternalProvider = effectiveProviderDescriptor
    ? !effectiveProviderDescriptor.onPremise
    : false;
  const credentialRequired = effectiveProviderDescriptor?.credentialRequired ?? false;
  const probeSupported = effectiveProviderDescriptor?.probeSupported ?? true;

  const modelBothRequiredError = draft && draft.provider && !draft.model?.trim();
  const externalNeededError =
    draft && isExternalProvider && !draft.externalProviderAllowed && !providerPending;
  const unknownProviderError =
    draft &&
    draft.provider &&
    providers &&
    !providers.some((p) => p.key === draft.provider) &&
    !providerPending;
  const canSave = !modelBothRequiredError && !externalNeededError && !unknownProviderError;

  const handleSave = () => {
    if (!draft || !query.data) return;
    const body = buildAiAgentUpdate(draft, query.data, providerPending, credentialCandidate);
    update.mutate(body, {
      onSuccess: () => {
        setProviderPending(false);
        setCredentialCandidate('');
        success(t('ai.saved'));
      },
      onError: (error) => {
        if (error.status === 403) {
          notifyError(t('ai.credentialForbidden'));
        } else {
          notifyError(error.message);
        }
        if (error.status === 409) void query.refetch();
      },
    });
  };

  const setModel = (provider: string, model: string) => {
    if (!draft) return;
    setProviderPending(false);
    form.patch({ provider: provider || null, model: model || null });
  };

  const useDefaultModel = () => {
    if (!query.data) return;
    if (query.data.provider !== null) {
      setProviderPending(true);
      form.patch({ provider: query.data.provider, model: query.data.model });
    } else {
      form.patch({ provider: null, model: null });
    }
  };
  const keepModel = () => {
    if (!query.data) return;
    setProviderPending(false);
    form.patch({ provider: query.data.provider, model: query.data.model });
  };
  const chooseOtherModel = () => {
    if (!query.data) return;
    form.patch({
      provider: query.data.platformDefaultProvider,
      model: query.data.platformDefaultModel,
    });
  };

  const setExternalAllowed = (value: boolean) => {
    if (value) {
      setExternalConfirmOpen(true);
    } else {
      form.setField('externalProviderAllowed', false);
    }
  };

  const checkedPairDiffers =
    draft &&
    query.data &&
    (providerPending
      ? query.data.provider !== query.data.platformDefaultProvider
      : draft.provider !== query.data.provider || draft.model !== query.data.model);

  const runCheck = () => {
    if (!draft) return;
    const body = checkedPairDiffers
      ? {
          provider: displayedProvider ?? query.data?.platformDefaultProvider,
          model: providerPending ? query.data?.platformDefaultModel : (draft.model ?? undefined),
        }
      : undefined;
    checkMutation.mutate(body);
  };

  const externalAllowedForBody = draft?.externalProviderAllowed ?? false;
  const showExternalSwitch = isExternalProvider || externalAllowedForBody;

  // Provider-check outcome quick actions (AI card doc §7.6) — the fields they jump to are rendered
  // above this card's own check panel, so a DOM id is enough; no ref plumbing needed.
  const focusModelField = () => {
    if (displayedProvider === null) {
      // The model-name input does not exist yet in this state (Platform default / Reset pending) —
      // switch to the editable "Chosen" state (pre-filled with the platform's own pair) first, then
      // focus once it has rendered.
      chooseOtherModel();
      requestAnimationFrame(() => document.getElementById('ai-model-name-input')?.focus());
      return;
    }
    document.getElementById('ai-model-name-input')?.focus();
  };
  const focusCredentialField = () => {
    const input = document.getElementById('ai-credential-input');
    input?.scrollIntoView({ block: 'center' });
    input?.focus();
  };
  const focusExternalSwitch = () => {
    const row = document.getElementById('ai-external-switch-row');
    row?.scrollIntoView({ block: 'center' });
    row?.querySelector('input')?.focus();
  };

  return (
    <SettingsCard
      title={t('ai.title')}
      description={t('ai.description')}
      icon={<HubRoundedIcon />}
      action={
        query.data ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={t('settings:scaffold.versionChip', { version: query.data.version })}
            />
            {formatTimestamp(query.data.updatedAt) ? (
              <Chip
                size="small"
                variant="outlined"
                label={t('settings:scaffold.updatedChip', {
                  value: formatTimestamp(query.data.updatedAt),
                })}
              />
            ) : null}
          </Stack>
        ) : undefined
      }
    >
      {query.isLoading ? (
        <LoadingState label={t('ai.title')} fullHeight={false} />
      ) : query.isError ? (
        <ErrorState message={query.error?.message} onRetry={() => void query.refetch()} />
      ) : draft && query.data ? (
        <Stack spacing={0.5}>
          {query.data.version === 0 ? <Alert severity="info">{t('ai.legacyNotice')}</Alert> : null}

          <AiStatusLine
            availability={{ aiCapability, channels: availabilityChannels }}
            isLoading={availabilityLoading}
            isError={availabilityError}
            enabled={query.data.enabled}
            effectiveAiMode={effectiveAiMode}
            effectiveOverriddenFields={effectiveOverriddenFields}
            canViewTenant={canViewTenant}
            onChangeAiMode={onChangeAiMode}
          />

          <SwitchField
            label={t('ai.enabled.label')}
            description={t('ai.enabled.description')}
            checked={draft.enabled}
            onChange={(value) => form.setField('enabled', value)}
            disabled={disabled}
          />

          <ModelField
            provider={displayedProvider}
            model={providerPending ? query.data.platformDefaultModel : draft.model}
            pending={providerPending}
            platformDefaultProvider={query.data.platformDefaultProvider}
            platformDefaultModel={query.data.platformDefaultModel}
            providers={providers}
            isLoading={dictionariesLoading}
            isError={dictionariesError}
            onRetry={onRetryDictionaries}
            externalProviderPlatformAllowed={query.data.externalProviderPlatformAllowed}
            disabled={disabled}
            onChange={setModel}
            onChooseOther={chooseOtherModel}
            onUseDefault={useDefaultModel}
            onKeepYours={keepModel}
          />

          {showExternalSwitch ? (
            <Stack id="ai-external-switch-row" spacing={0.5}>
              <SwitchField
                label={t('ai.external.label')}
                description={t('ai.external.description')}
                checked={draft.externalProviderAllowed}
                onChange={setExternalAllowed}
                disabled={disabled || !query.data.externalProviderPlatformAllowed}
              />
              {!query.data.externalProviderPlatformAllowed ? (
                <Alert severity="info" icon={false}>
                  {t('locked.platformHelper', { feature: t('locked.features.externalProviders') })}
                </Alert>
              ) : externalNeededError ? (
                <Alert severity="warning">{t('ai.external.needed')}</Alert>
              ) : null}
            </Stack>
          ) : null}

          {credentialRequired ? (
            <>
              <ConfiguredSecretField
                id="ai-credential-input"
                label={t('ai.apiKey.label')}
                description={t('ai.apiKey.description')}
                configured={query.data.credentialConfigured}
                masked={query.data.credentialMasked}
                secret={credentialCandidate}
                onSecretChange={setCredentialCandidate}
                disabled={disabled || !canManageCredentials}
              />
              {!canManageCredentials ? (
                <Alert severity="info">{t('ai.credentialsRestricted')}</Alert>
              ) : null}
              {credentialRequired &&
              !query.data.credentialConfigured &&
              credentialCandidate.trim() === '' ? (
                <Alert severity="warning">{t('ai.apiKey.missing')}</Alert>
              ) : null}
            </>
          ) : null}

          {canManage ? (
            <ProviderCheckPanel
              probeSupported={probeSupported}
              disabled={!canManage}
              onCheck={runCheck}
              isPending={checkMutation.isPending}
              result={checkMutation.data}
              requestError={
                checkMutation.isError && checkMutation.error.status !== 403
                  ? checkMutation.error.message
                  : undefined
              }
              forbidden={checkMutation.isError && checkMutation.error.status === 403}
              stale={Boolean(checkedPairDiffers) && Boolean(checkMutation.data)}
              usesUnsavedCredentials={
                credentialCandidate.trim() !== '' ||
                draft.externalProviderAllowed !== query.data.externalProviderAllowed
              }
              externalProviderPlatformAllowed={query.data.externalProviderPlatformAllowed}
              onEditModel={focusModelField}
              onEnterKey={credentialRequired ? focusCredentialField : undefined}
              onFocusExternalSwitch={focusExternalSwitch}
            />
          ) : null}

          {canManage ? (
            <SecondaryFormActions
              dirty={dirty}
              saving={update.isPending}
              dirtyLabel={t('settings:scaffold.unsavedChanges')}
              cleanLabel={t('settings:scaffold.allChangesSaved')}
              saveLabel={t('ai.saveLabel')}
              onSave={handleSave}
              onReset={reset}
              saveDisabled={!canSave}
            />
          ) : null}
        </Stack>
      ) : null}
      <FeedbackSnackbar feedback={feedback} onClose={clear} />

      <Dialog open={externalConfirmOpen} onClose={() => setExternalConfirmOpen(false)}>
        <DialogTitle>{t('ai.external.confirm.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('ai.external.confirm.body', {
              provider: displayedProvider
                ? t(`ai.providers.${displayedProvider}`, displayedProvider)
                : '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExternalConfirmOpen(false)} autoFocus>
            {t('common:action.cancel')}
          </Button>
          <Button
            onClick={() => {
              form.setField('externalProviderAllowed', true);
              setExternalConfirmOpen(false);
            }}
          >
            {t('ai.external.confirm.allow')}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsCard>
  );
}
